import React from 'react';
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";

interface HalfRingProgressProps {
    data: ProgressData;
    radius?: number;
    stroke?: number;
    currentStage: number;
}

interface ProgressData {
    finished_stages: number;
    late_stages: number;
    over_year_late_stages: number;
    in_progress_stages: number;
    not_started_stages: number;
    days_late: number;
    current_stage: any;
}

const HalfProgressComponent: React.FC<HalfRingProgressProps> = ({
    data: {
        finished_stages,
        late_stages,
        over_year_late_stages,
        in_progress_stages,
        not_started_stages,
        days_late,
        current_stage
    },
    currentStage = 0,
    radius = 60,
    stroke = 10,
}) => {
    const totalSections = not_started_stages + finished_stages + late_stages + over_year_late_stages + in_progress_stages;
    const completed = totalSections === 0 ? 0 : Math.round((finished_stages * 100 / totalSections));
    const inProgress = totalSections === 0 ? 0 : Math.round((in_progress_stages * 100 / totalSections));

    const total = completed + inProgress;
    const notStarted = Math.max(0, 100 - total);

    const segments = [
        { percent: completed, color: '#5ECCA3' },   // green
        { percent: inProgress, color: '#007FFF' },  // blue
        { percent: notStarted, color: '#AFBDD3' },  // gray
    ];

    const lateBgColor = days_late > 365 ? '#FE6F5C' : days_late > 0 ? '#FFBD06' : ''

    let startAngle = 180; // start from the left (9 o'clock), going clockwise

    const pathElements = segments
        .filter(seg => seg.percent > 0)
        .map((seg, i) => {
            const angle = (seg.percent / 100) * 180; // each segment's arc in degrees
            const endAngle = startAngle + angle;

            const path = describeArc(
                radius,
                radius,
                radius - stroke / 2,
                startAngle,
                endAngle
            );

            const element = (
                <path
                    key={i}
                    d={path}
                    stroke={seg.color}
                    strokeWidth={stroke}
                    fill="none"
                    strokeLinecap="round"
                />
            );

            startAngle = endAngle; // update for next segment
            return element;
        });

    return (
        <Box display={'flex'} justifyContent={'center'} position={'relative'}>
            {lateBgColor &&
                <Box sx={{ position: 'absolute', right: 'calc(50% - ' + radius + 'px' + ' - 25px)', bottom: 0, width: '20px', height: '20px', textAlign: 'center', borderRadius: '50%', color: '#0C3768', background: lateBgColor }}>i</Box>
            }
            <svg
                aria-label={`${notStarted}% טרם החל, ${inProgress}% בתהליך, ${completed}% הסתיים`}
                width={radius * 2}
                height={radius + stroke}
                viewBox={`0 0 ${radius * 2} ${radius + stroke}`}
            >
                {pathElements}
            </svg>
            <Box position={"absolute"} bottom={-20} textAlign={'center'}>
                <Typography gutterBottom variant="h6" component="div" sx={{ color: '#0C3768', fontWeight: 'bold' }}>שלב {currentStage}/{totalSections}</Typography>
                <Typography gutterBottom component="div"
                    sx={{ color: '#0C3768', fontWeight: '400', fontSize: '14px' }}>{totalSections === not_started_stages ? 'טרם התחיל' : current_stage.name ? current_stage.name : 'בביצוע'}</Typography>
            </Box>
        </Box>
    );
};

// SVG Arc Helper Functions
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(angleRad),
        y: cy + r * Math.sin(angleRad),
    };
}

function describeArc(
    cx: number,
    cy: number,
    r: number,
    startAngle: number,
    endAngle: number
) {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return [
        'M', start.x, start.y,
        'A', r, r, 0, largeArcFlag, 0, end.x, end.y,
    ].join(' ');
}

export default HalfProgressComponent;